<script setup lang="ts">
import { DocumentState } from "~/types/enums/DocumentState";

const documentId = inject("documentId") as string;
const { submittedDocument } = storeToRefs(useSubmittedDocumentStore(documentId));
const corrections = useDocumentTeacherCommentCorrections(documentId);
const hasPendingCorrections = ref(false);
const isContinuing = ref(false);

watch(corrections, (items) => {
  if (items.length > 0) hasPendingCorrections.value = true;
}, { immediate: true });

const continueToChecklist = async () => {
  if (corrections.value.length > 0 || isContinuing.value || !submittedDocument.value) return;
  isContinuing.value = true;
  try {
    // A paper reopened directly in review must still visit the writing checklist.
    if ([DocumentState.studentReview, DocumentState.studentCompleted].includes(submittedDocument.value.state)) {
      await useDocuments().updateDocumentStateById(documentId, DocumentState.editing);
      submittedDocument.value.state = DocumentState.editing;
    }
    // Undo can restore comments while the document state update is in flight.
    hasPendingCorrections.value = corrections.value.length > 0;
  } catch {
    useBaseToast("The checklist could not be opened. Please try again.", "error");
  } finally {
    isContinuing.value = false;
  }
};
</script>

<template>
  <Transition
    mode="out-in"
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="translate-y-3 opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="translate-y-0 opacity-100"
    leave-to-class="-translate-y-3 opacity-0"
  >
    <div
      v-if="hasPendingCorrections"
      key="guided-corrections"
      class="h-full min-h-0 w-[500px] shrink-0 overflow-y-auto bg-surface pb-2"
    >
      <DocumentSelfAssessmentGuidedCorrections
        :is-continuing="isContinuing"
        @continue="continueToChecklist"
      />
    </div>
    <div v-else key="checklist" class="h-full min-h-0">
      <slot />
    </div>
  </Transition>
</template>
